import axios from "axios";
import type { DAOCoinLimitOrderEntryResponse } from "deso-protocol";

import { isLocalBackend } from "@/backend/api-helpers";
import { DESO_DENOMINATED_TOKENS } from "@/utils/constants";

const baseURL = `${process.env.NEXT_PUBLIC_CORE_DESO_NODE}/api/v0`;

const desoAPIInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

export interface GetQuoteCurrencyPriceInUsdResponse {
  BestAsk: string;
  BestBid: string;
  MidPrice: string;
}

export const getCoinPriceInUsd = (ProfilePublicKeyBase58Check: string) => {
  if (isLocalBackend()) {
    // If we are running a local backend, return a dummy response. These values can be adjusted as needed for testing.
    return {
      data: {
        BestAsk: "0",
        BestBid: "0",
        MidPrice: "0",
      },
    };
  }

  return desoAPIInstance.post<GetQuoteCurrencyPriceInUsdResponse>(
    "get-quote-currency-price-in-usd",
    {
      QuoteCurrencyPublicKeyBase58Check: ProfilePublicKeyBase58Check,
    },
  );
};

export interface GetDaoCoinMarketFeesResponse {
  FeeBasisPointsByPublicKey: Record<string, number>;
}

export const getDaoCoinMarketFees = (ProfilePublicKeyBase58Check: string) => {
  return desoAPIInstance.post<GetDaoCoinMarketFeesResponse>(
    "get-dao-coin-market-fees",
    {
      ProfilePublicKeyBase58Check,
    },
  );
};

export type LockedYieldCurvePoint = {
  LockupDurationNanoSecs: number;
  LockupYieldAPYBasisPoints: number;
  ProfileEntryResponse: {
    BestExchangeRateDESOPerDAOCoin: number;
    CoinEntry: {
      CreatorBasisPoints: number;
      DeSoLockedNanos: number;
      NumberOfHolders: number;
      CoinsInCirculationNanos: number;
      CoinWatermarkNanos: number;
    };
    CoinPriceBitCloutNanos: number;
    CoinPriceDeSoNanos: number;
    Comments: string | null;
    DAOCoinEntry: {
      NumberOfHolders: number;
      CoinsInCirculationNanos: string;
      MintingDisabled: boolean;
      TransferRestrictionStatus: string;
      LockupTransferRestrictionStatus: string;
    };
    DESOBalanceNanos: number;
    Description: string;
    ExtraData: {
      DAOPublicKeysPurchased: string;
      TokenTradingFeesMap: string;
    };
    IsFeaturedTutorialUpAndComingCreator: boolean;
    IsFeaturedTutorialWellKnownCreator: boolean;
    IsHidden: boolean;
    IsReserved: boolean;
    IsVerified: boolean;
    // TODO: update type
    Posts: null;
    PublicKeyBase58Check: string;
    Username: string;
    // TODO: update type
    UsersThatHODL: null;
    ProfilePublicKeyBase58Check: string;
  };
};

type GetLockYieldCurvePointsResponse = Array<LockedYieldCurvePoint>;

export const getLockYieldCurvePoints = async ({
  CreatorsPublicKeyBase58Check,
}: {
  CreatorsPublicKeyBase58Check: string;
}) => {
  const response = await desoAPIInstance.get<GetLockYieldCurvePointsResponse>(
    `lockup-yield-curve-points/${CreatorsPublicKeyBase58Check}`,
  );
  return response.data;
};

export interface BaseCurrencyPriceEntry {
  QuoteCurrencyPriceInUsd: number;
  MidPriceInQuoteCurrency: number;
  MidPriceInUsd: number;
  BestAskInQuoteCurrency: number;
  BestAskInUsd: number;
  BestBidInQuoteCurrency: number;
  BestBidInUsd: number;
  ReceiveAmountInQuoteCurrency: string;
  ReceiveAmountInUsd: string;
  ExecutionAmountInBaseCurrency: string;
  ExecutionPriceInQuoteCurrency: string;
  ExecutionPriceInUsd: string;
}

export interface BaseCurrencyPriceResponse {
  Entries: Array<BaseCurrencyPriceEntry>;
}

export const getBaseCurrencyPrice = async (
  priceEntries: Array<{
    BaseCurrencyPublicKeyBase58Check: string;
    BaseCurrencyQuantityToSell: number;
  }>,
) => {
  const response = await desoAPIInstance.post<BaseCurrencyPriceResponse>(
    `get-base-currency-price`,
    {
      Entries: priceEntries.map(
        ({ BaseCurrencyPublicKeyBase58Check, BaseCurrencyQuantityToSell }) => {
          const isDesoDenominated = DESO_DENOMINATED_TOKENS.includes(
            BaseCurrencyPublicKeyBase58Check,
          );

          return {
            BaseCurrencyPublicKeyBase58Check,
            QuoteCurrencyPublicKeyBase58Check: isDesoDenominated
              ? process.env.NEXT_PUBLIC_DESO_ZERO_PUBLIC_KEY
              : process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
            BaseCurrencyQuantityToSell,
          };
        },
      ),
    },
  );
  return response.data.Entries;
};

export const getDaoCoinLimitOrders = async (payload: {
  DAOCoin1CreatorPublicKeyBase58Check: string;
  DAOCoin2CreatorPublicKeyBase58Check: string;
}): Promise<{ Orders: Array<DAOCoinLimitOrderEntryResponse> }> => {
  const response = await desoAPIInstance.post(
    `get-dao-coin-limit-orders`,
    payload,
  );
  return response.data;
};
